import { Injectable } from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  UntypedFormArray,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import {
  Parameters,
  PledgeProductRead,
} from '@app/forms/subscription/definition/interface/Pledge';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import FormGroupArray from '@app/forms/subscription/services/FormGroupArray';
import * as _ from 'lodash';

function clamp(n: number, min: number, max: number): number {
  const v = Number(n);
  if (isNaN(v)) return min;
  return Math.max(min, Math.min(max, v));
}

function roundTo(n: number, digits: number): number {
  const v = Number(n);
  if (isNaN(v)) return 0;
  return Number(v.toFixed(digits));
}

function maxFractionDigits(maxDigits: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;
    const s = String(raw).replace(',', '.');
    if (!/^\d*\.?\d*$/.test(s)) return { number: true };
    const [, decimals] = s.split('.');
    if (decimals && decimals.length > maxDigits) {
      return { maxFractionDigits: { max: maxDigits } };
    }
    return null;
  };
}

function maxBySibling(siblingKey: string, digits = 4): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const parent = control.parent as UntypedFormGroup | null;
    if (!parent) return null;

    const maxRaw = Number(parent.get(siblingKey)?.value ?? 0);
    const max = roundTo(maxRaw, digits);
    const val = Number(control.value);

    if (isNaN(val)) return null;
    if (val < 0) return { min: { min: 0 } };
    if (val > max) return { max: { max } };
    return null;
  };
}

@Injectable({ providedIn: 'root' })
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  generateForm(entity: PledgeProductRead & { parameters: any }, option) {
    const parameters: Parameters = { ...option };
    const options: AbstractControlOptions = {};

    const supportsDivestment: UntypedFormGroup[] = [];
    const currentRepartition = entity?.parameters?.supportsDivestment || [];

    if (currentRepartition) {
      _.forEach(currentRepartition, support =>
        supportsDivestment.push(
          this.formBuilder.group(this.createModel(support, entity)),
        ),
      );
    }

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        currentAmountStock: [
          entity?.parameters?.currentSupportRepartition?.totalAmount || null,
        ],
        arbitrageType: [entity?.parameters?.arbitrageType || null],
        supportsDivestment: this.formBuilder.array(
          supportsDivestment,
          Validators.required,
        ),
      }),
    };

    const productForm = this.formBuilder.group(formConfig, options);
    this.attachDivestmentSyncProduct(productForm);
    return productForm;
  }

  generate(entity: any, option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }

  public createModel(item, entity) {
    const isFlux = entity?.parameters?.arbitrageType === 'FLUX';

    return {
      id: [item ? item.id : null],
      name: [item ? item.name : null],
      type: [item ? item.type : null],
      isin: [item ? item.isin : null],
      details: [item ? item.details : null],

      repartitionStock: [
        item && !_.isNil(item.repartitionStock)
          ? roundTo(item.repartitionStock * 100, 4)
          : 0,
        isFlux
          ? [Validators.nullValidator]
          : [
              Validators.required,
              Validators.min(0),
              Validators.max(100),
              maxFractionDigits(4),
            ],
      ],

      repartitionFlux: [
        item && item.repartitionFlux ? item.repartitionFlux : null,
      ],

      amountRepartitionStock: [
        item && item.amountRepartitionStock
          ? roundTo(item.amountRepartitionStock, 4)
          : 0,
        isFlux
          ? [Validators.nullValidator]
          : [
              Validators.required,
              maxBySibling('currentAllocationAmountStock', 4),
            ],
      ],

      amountRepartitionFlux: [
        item && item.amountRepartitionFlux ? item.amountRepartitionFlux : null,
      ],

      currentAllocationAmountStock: [
        item && item.currentAllocationAmountStock
          ? roundTo(item.currentAllocationAmountStock, 4)
          : 0,
      ],
    };
  }

  private attachDivestmentSyncProduct(product: UntypedFormGroup) {
    const params = product.get('parameters') as UntypedFormGroup;
    if (!params) return;

    const supports = params.get('supportsDivestment') as UntypedFormArray;
    if (!supports || !supports.length) return;

    supports.controls.forEach((ctrl: UntypedFormGroup) => {
      const percentCtrl = ctrl.get('repartitionStock');
      const amountCtrl = ctrl.get('amountRepartitionStock');
      const currentCtrl = ctrl.get('currentAllocationAmountStock');
      if (!percentCtrl || !amountCtrl || !currentCtrl) return;

      let syncing = false;

      percentCtrl.valueChanges.subscribe((raw: any) => {
        if (syncing) return;
        syncing = true;

        const current = Number(currentCtrl.value) || 0;

        let p = Number(String(raw).replace(',', '.'));
        if (isNaN(p)) p = 0;
        p = clamp(p, 0, 100);
        const p4 = roundTo(p, 4);

        if (p4 !== Number(raw)) {
          percentCtrl.setValue(p4, { emitEvent: false });
        }

        const max4 = roundTo(current, 4);
        const amount = Math.min(roundTo(current * (p4 / 100), 4), max4);
        amountCtrl.setValue(amount, { emitEvent: false });
        amountCtrl.updateValueAndValidity({ emitEvent: false });

        syncing = false;
      });

      amountCtrl.valueChanges.subscribe((raw: any) => {
        if (syncing) return;
        syncing = true;

        const current = Number(currentCtrl.value) || 0;

        let amt = Number(String(raw).replace(',', '.'));
        if (isNaN(amt)) amt = 0;
        amt = clamp(amt, 0, roundTo(current, 4));
        const amt4 = roundTo(amt, 4);

        if (amt4 !== Number(raw)) {
          amountCtrl.setValue(amt4, { emitEvent: false });
        }

        const p = current > 0 ? (amt4 / current) * 100 : 0;
        const p4 = roundTo(clamp(p, 0, 100), 4);

        percentCtrl.setValue(p4, { emitEvent: false });
        percentCtrl.updateValueAndValidity({ emitEvent: false });

        syncing = false;
      });

      currentCtrl.valueChanges.subscribe(() => {
        const p = Number(percentCtrl.value) || 0;
        const current = Number(currentCtrl.value) || 0;
        const amount = roundTo(current * (clamp(p, 0, 100) / 100), 4);
        amountCtrl.setValue(amount, { emitEvent: false });
        amountCtrl.updateValueAndValidity({ emitEvent: false });
      });

      const initP = clamp(Number(percentCtrl.value) || 0, 0, 100);
      percentCtrl.setValue(roundTo(initP, 4), { emitEvent: true });
    });
  }

  get arbitrageType(): string {
    return this.form
      .get('products')
      .get(`${0}`)
      .get('parameters')
      .get('arbitrageType').value;
  }
  get products() {
    return this.form.get('products') as UntypedFormArray;
  }
  get supportsDivestment() {
    return this.products
      .get(`${0}`)
      .get('parameters')
      .get('supportsDivestment') as UntypedFormArray;
  }

  extract() {
    const products = _.cloneDeep(this.form.value.products);
    products.forEach(product => {
      const supportsDivestment = _.cloneDeep(
        product.parameters.supportsDivestment,
      );
      supportsDivestment.forEach(support => {
        if (
          product.parameters.arbitrageType !== 'FLUX' &&
          !_.isNil(support.amountRepartitionStock) &&
          !_.isNil(support.currentAllocationAmountStock) &&
          Number(support.currentAllocationAmountStock) > 0
        ) {
          const p =
            (Number(support.amountRepartitionStock) /
              Number(support.currentAllocationAmountStock)) *
            100;
          support.repartitionStock = roundTo(clamp(p, 0, 100), 4);
        }

        support.repartitionStock =
          product.parameters.arbitrageType !== 'FLUX' &&
          !_.isNil(support.repartitionStock)
            ? Number(support.repartitionStock) / 100
            : 0;

        support.amountRepartitionStock =
          product.parameters.arbitrageType !== 'FLUX' &&
          !_.isNil(support.repartitionStock) &&
          !_.isNil(support.currentAllocationAmountStock)
            ? roundTo(
                Number(support.currentAllocationAmountStock) *
                  Number(support.repartitionStock),
                4,
              )
            : 0;
      });
      product.parameters.supportsDivestment = supportsDivestment;
    });
    return products;
  }
}
